import React, { useEffect, useState } from 'react';
import { Layout, Badge, Dropdown, Avatar, Button, Drawer, Space, Tooltip } from 'antd';
import {
  BellOutlined,
  LogoutOutlined,
  MobileOutlined,
  SunOutlined,
  GlobalOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { storeApi, notificationApi } from '../../api';
import { useLocale } from '../../context/LocaleContext';
import { useTheme } from '../../context/ThemeContext';

const { Header, Content } = Layout;

const getMenuItems = (isFinance: boolean, t: (key: string) => string): Array<{ key: string; label: string }> => {
  if (isFinance) {
    return [
      { key: '/finance/orders', label: t('财务审核') },
    ];
  }
  return [
    { key: '/', label: t('首页') },
    { key: '/orders', label: t('我的订单') },
    { key: '/shop', label: t('成衣商城') },
    { key: '/customers', label: t('我的客户') },
    { key: '/bills', label: t('我的账单') },
  ];
};

const StoreLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isStoreCustomer, isFinance } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { isDark, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [storeLogo, setStoreLogo] = useState<string>('');
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const brandName = user?.store_name || user?.username || '客户姓名';

  useEffect(() => {
    document.title = `${brandName} OMS`;
  }, [brandName]);

  useEffect(() => {
    const fetchUnread = () => {
      notificationApi.unreadCount()
        .then((res: any) => { if (res.code === 0) setUnreadCount(res.data.unread_count || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 60000);
    return () => clearInterval(timer);
  }, [location.pathname]);

  const menuItems = getMenuItems(isFinance, t);
  const selectedKey = React.useMemo(() => {
    if (location.pathname === '/') return '/';
    if (isFinance) {
      const parts = location.pathname.split('/');
      if (parts[1] === 'finance') return `/${parts[1]}/${parts[2]}`;
    }
    return `/${location.pathname.split('/')[1]}`;
  }, [location.pathname, isFinance]);

  const userMenuItems = [
    { key: 'logout', icon: <LogoutOutlined />, label: t('退出登录'), danger: true },
  ];

  const langMenuItems = [
    { key: 'zh', label: locale === 'zh' ? <b style={{ color: '#1a5fb4' }}>中文</b> : '中文', onClick: () => setLocale('zh') },
    { key: 'en', label: locale === 'en' ? <b style={{ color: '#1a5fb4' }}>English</b> : 'English', onClick: () => setLocale('en') },
  ];

  const handleUserMenu = ({ key }: { key: string }) => {
    if (key === 'logout') logout();
  };

  const handleNavClick = (key: string) => {
    navigate(key);
    setMobileOpen(false);
  };

  const renderNavItems = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
      {menuItems.map((item) => {
        const isActive = selectedKey === item.key;
        return (
          <div
            key={item.key}
            onClick={() => handleNavClick(item.key)}
            style={{
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? '#1a5fb4' : isDark ? '#fff' : '#333',
              position: 'relative',
              height: 64,
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.2s',
            }}
          >
            {item.label}
            {isActive && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '10%',
                  right: '10%',
                  height: 3,
                  background: '#1a5fb4',
                  borderRadius: '2px 2px 0 0',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const headerStyle: React.CSSProperties = {
    background: isDark ? '#141414' : '#fff',
    padding: '0 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${isDark ? '#303030' : '#e8e8e8'}`,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 64,
    zIndex: 100,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  };

  const userRoleLabel = isFinance ? t('财务经理') : isStoreCustomer ? t('门店经理') : t('经理');

  const fetchStoreLogo = () => {
    if (user?.store_id) {
      storeApi.storeDetail(user.store_id).then((res: any) => {
        if (res.code === 0 && res.data?.logo_url) {
          setStoreLogo(res.data.logo_url);
        } else {
          setStoreLogo('');
        }
      }).catch(() => {});
    }
  };

  useEffect(() => {
    fetchStoreLogo();
    const handler = () => fetchStoreLogo();
    window.addEventListener('store-updated', handler);
    return () => window.removeEventListener('store-updated', handler);
  }, [user?.store_id]);

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden', paddingTop: 64, background: isDark ? '#000' : '#f0f2f5' }}>
      {/* Mobile Drawer */}
      <Drawer
        placement="left"
        closable={false}
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        styles={{ body: { padding: 0 } }}
        width={200}
      >
        <div style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 700, color: '#1a5fb4' }}>
          {storeLogo ? (
            <img src={storeLogo} alt="logo" style={{ height: 40, objectFit: 'contain' }} />
          ) : (
            `${brandName} OMS`
          )}
        </div>
        <div style={{ padding: '8px 0' }}>
          {menuItems.map((item) => {
            const isActive = selectedKey === item.key;
            return (
              <div
                key={item.key}
                onClick={() => handleNavClick(item.key)}
                style={{
                  padding: '12px 24px',
                  cursor: 'pointer',
                  color: isActive ? '#1a5fb4' : '#333',
                  fontWeight: isActive ? 600 : 400,
                  background: isActive ? '#e6f7ff' : 'transparent',
                }}
              >
                {item.label}
              </div>
            );
          })}
        </div>
      </Drawer>

      {/* Desktop & Mobile Header */}
      <Header style={headerStyle}>
        {/* Left: Logo + Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 48 }} className="desktop-only">
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: '#1a5fb4',
              cursor: 'pointer',
              letterSpacing: 0.5,
              fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => navigate('/')}
          >
            {storeLogo ? (
              <img src={storeLogo} alt="logo" style={{ height: 40, objectFit: 'contain' }} />
            ) : (
              `${brandName} OMS`
            )}
          </div>
          {renderNavItems()}
        </div>

        {/* Mobile Left: Hamburger */}
        <div className="mobile-only">
          <Button
            type="text"
            icon={<MobileOutlined />}
            onClick={() => setMobileOpen(true)}
            style={{ color: isDark ? '#fff' : '#000' }}
          />
        </div>

        {/* Mobile Logo (center-ish) */}
        <div
          className="mobile-only"
          style={{
            fontSize: 18,
            fontWeight: 800,
            color: '#1a5fb4',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          {storeLogo ? (
            <img src={storeLogo} alt="logo" style={{ height: 32, objectFit: 'contain' }} />
          ) : (
            `${brandName}`
          )}
        </div>

        {/* Right: Tools */}
        <Space size={20} align="center">
          {/* Language */}
          <Dropdown menu={{ items: langMenuItems }} placement="bottomRight">
            <GlobalOutlined style={{ fontSize: 18, color: isDark ? '#ccc' : '#555', cursor: 'pointer' }} />
          </Dropdown>

          <Tooltip title={t('通知')}>
            <Badge count={unreadCount} size="small" overflowCount={99}>
              <BellOutlined
                style={{ fontSize: 18, color: isDark ? '#fff' : '#666', cursor: 'pointer' }}
                onClick={() => navigate('/notifications')}
              />
            </Badge>
          </Tooltip>
          <Tooltip title={t('设置')}>
            <SettingOutlined
              style={{ fontSize: 18, color: isDark ? '#fff' : '#666', cursor: 'pointer' }}
              onClick={() => navigate('/account')}
            />
          </Tooltip>

          {/* Divider */}
          <div
            style={{
              width: 1,
              height: 24,
              background: isDark ? '#303030' : '#e0e0e0',
              margin: '0 4px',
            }}
          />

          {/* User Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'right' }} className="desktop-only">
              <div style={{ fontSize: 14, fontWeight: 600, color: isDark ? '#fff' : '#333', lineHeight: 1.3 }}>
                {user?.username || user?.real_name}
              </div>
              <div style={{ fontSize: 11, color: '#999', lineHeight: 1.3, letterSpacing: 0.5 }}>
                {userRoleLabel}
              </div>
            </div>
            <Dropdown menu={{ items: userMenuItems as any, onClick: handleUserMenu }} placement="bottomRight">
              <Avatar size="default" style={{ background: '#e8e8e8', color: '#666', cursor: 'pointer' }}>
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </Dropdown>
          </div>
        </Space>
      </Header>

      {/* Content Area */}
      <Content style={{ padding: 24, background: isDark ? '#000' : '#f0f2f5', height: 'calc(100vh - 64px)', overflowY: 'auto' }}>
        <div className="jd-container" style={{ width: '100%' }}>
          {children}
        </div>
      </Content>
    </Layout>
  );
};

export default StoreLayout;
